import {
  has,
  isNil,
  isPlainObject,
  reduce,
  find,
  filter,
  map,
  mapValues,
  compact,
  toInteger,
  flattenDeep,
} from 'lodash';
import jsonpath from 'jsonpath';
import { REF_RESOURCES_ENABLED } from 'js/constants/config';
import { RESOURCE_TYPE_FLOW } from '../constants/resourceTypes';
import { ACL_RESOURCE_TYPE_FLOW } from '../constants/aclConstants';
import { CONNECTOR_CODE_SQL_RUNNER } from '../constants/connectionCategories';

import { getDataMapIdsFromCustomCode } from './utils/dataMapUtils';
import { getCredentialIdsFromCustomCode } from './utils/credentialUtils';

const REFERENCED_RESOURCE_PATHS = {
  data_sets: [],
  data_maps: [
    '$["rest.iterations"][*]["map.id"]', // requesting REST samples
    '$.source_config["rest.iterations"][*]["map.id"]', // creating REST source
    '$.transform.data_maps', // using lookups in JOLT,
    getDataMapIdsFromCustomCode, // using lookup IDs in custom code
  ],
  data_credentials: [
    // creating ELT Script source
    (payload) =>
      filter(payload?.script_config?.config, (value, key) => key?.startsWith?.('credentials.id_')),
    getCredentialIdsFromCustomCode, // using getCredential(id) in custom code
  ],
  code_containers: [
    // using reusable attr tx
    (payload) =>
      map(
        find(payload?.transform?.transforms, { operation: 'nexla.modify' })?.spec,
        (spec) =>
          spec?.startsWith?.('=custom(') &&
          toInteger(spec?.substring?.('=custom('.length, spec.indexOf(','))),
      ),
  ],
};

export const setReferencedResourceIds = (payload) => {
  //TODO: Remove feature flag check once feature is enabled for all users
  if (REF_RESOURCES_ENABLED === false || !isPlainObject(payload)) {
    return payload;
  }

  return {
    ...payload,
    referenced_resource_ids: mapValues(REFERENCED_RESOURCE_PATHS, (paths) =>
      compact(
        flattenDeep(
          paths.map((path) => {
            if (typeof path === 'function') {
              return path(payload);
            }

            if (typeof path === 'string') {
              return jsonpath.query(payload, path);
            }

            return [];
          }),
        ),
      ),
    ),
  };
};

export const isDetectedDataset = (dataSet) => {
  let isSourceDataSet = false;
  if (has(dataSet, 'is_detected')) {
    isSourceDataSet = dataSet.is_detected;
  } else if (
    (has(dataSet, 'data_source_id') && !isNil(dataSet.data_source_id)) ||
    (has(dataSet, 'data_source') && !isNil(dataSet.data_source))
  ) {
    isSourceDataSet = true;
  }
  return isSourceDataSet;
};

/**
 * Checks if the resource is a flow node resource
 * @param {string|Object} resourceTypeOrData - The resource type (string) or data object
 * @param {Object} [data] - The data object (optional, only if first param is resourceType)
 * @returns {boolean} - True if the resource is a flow node
 *
 * @example
 * // With resource type
 * isFlowNodeResource('flows', data)
 * isFlowNodeResource(ACL_RESOURCE_TYPE_FLOW, data)
 *
 * // With data only
 * isFlowNodeResource(data)
 */
export const isFlowNodeResource = (resourceTypeOrData, data = null) => {
  // If second argument is provided, first is resourceType
  if (data !== null && data !== undefined) {
    const resourceType = resourceTypeOrData;
    return (
      resourceType === RESOURCE_TYPE_FLOW ||
      resourceType === ACL_RESOURCE_TYPE_FLOW ||
      resourceType === 'flows' ||
      data?.resource_type === 'flow' ||
      data?.resourceType === 'flows'
    );
  }

  // Otherwise, first argument is data
  const dataObj = resourceTypeOrData;
  return dataObj?.resource_type === 'flow' || dataObj?.resourceType === 'flows';
};

export const isPublicDataset = (dataSet) => {
  return has(dataSet, 'public') && dataSet.public === true ? true : false;
};

/**
 * Returns true when the source's vendor is sql_runner (SQL Automation).
 */
export const isSqlAutomation = (data, vendors) => {
  if (!data || !vendors) return false;
  let vendorId;
  if (has(data, 'vendor_id') && !isNil(data.vendor_id)) {
    vendorId = data.vendor_id;
  } else if (
    has(data, 'source_config') &&
    has(data.source_config, 'vendor_id') &&
    !isNil(data.source_config.vendor_id)
  ) {
    vendorId = data.source_config.vendor_id;
  } else {
    return false;
  }
  return has(vendors, vendorId) && vendors[vendorId].name === CONNECTOR_CODE_SQL_RUNNER;
};

export const getConnectionOrVendorType = (
  data,
  vendors,
  defaultKey = null,
  vendorIdKey = 'vendorId',
) => {
  let defaultConnectionType = defaultKey && has(data, defaultKey) ? data[defaultKey] : null;
  let vendorId;

  if (has(data, vendorIdKey) && !isNil(data[vendorIdKey])) {
    vendorId = data[vendorIdKey];
  } else if (
    has(data, `source_config['${vendorIdKey}']`) &&
    !isNil(data.source_config[vendorIdKey])
  ) {
    vendorId = data.source_config[vendorIdKey];
  } else if (has(data, `sink_config['${vendorIdKey}']`) && !isNil(data.sink_config[vendorIdKey])) {
    vendorId = data.sink_config[vendorIdKey];
  }

  if (vendorId && has(vendors, vendorId)) {
    const vendorName = vendors[vendorId].name;
    if (vendorName === CONNECTOR_CODE_SQL_RUNNER) {
      return defaultConnectionType;
    }
    defaultConnectionType = vendorName;
  }

  return defaultConnectionType;
};

export const getResourceApiKeys = (data, apiKeys) => {
  let resourceKeys = null;

  if (has(data, 'api_keys') && !isNil(apiKeys)) {
    resourceKeys = reduce(
      data.api_keys,
      (acc, k) => {
        if (has(apiKeys, k)) {
          const thisKey = apiKeys[k];
          acc[k] = {
            id: k,
            name: thisKey.name,
            api_key: thisKey.api_key,
            url: thisKey.url,
            status: thisKey.status,
          };
        }

        return acc;
      },
      {},
    );
  }

  return resourceKeys;
};
